import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { fmtDate, euro } from '../../utils/formatUtils.js';
import { amsterdamDateStr, todayStr, toAmsterdamTime } from '../../utils/dateUtils.js';
import { fetchActiecentrumData, telOpenActies } from '../../utils/actiesUtils.js';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useActionsCount } from '../../contexts/ActionsCountContext';

const EMPTY_DATA = {
  afgewezenTokens: [], verlopenTokens: [], prospectActies: [], incasso: [], ptAanvragen: [],
};

export default function ActiesPage() {
  const { user, role } = useAuth();
  const { showError } = useToast();
  const { setCount } = useActionsCount();
  const navigate = useNavigate();
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissingId, setDismissingId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const nextData = await fetchActiecentrumData(user.id, role);
      setData(nextData);
      setCount(totalCount(nextData));
    } catch (err) {
      setError(t('acties.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, [setCount, user?.id, role]);

  useEffect(() => { load(); }, [load]);

  async function handleDismiss(actionType, referenceId) {
    const key = `${actionType}:${referenceId}`;
    setDismissingId(key);
    try {
      const { error: dismissError } = await supabase.from('dismissed_actions').insert({
        coach_id: user.id,
        action_type: actionType,
        reference_id: referenceId,
      });
      if (dismissError) throw dismissError;
      await load();
    } catch (err) {
      showError(t('acties.foutAfhandelen') + err.message);
    } finally {
      setDismissingId(null);
    }
  }

  const total = totalCount(data);
  const urgent = data.prospectActies.length + data.ptAanvragen.length;
  const exceptions = data.afgewezenTokens.length + data.verlopenTokens.length + data.incasso.length;

  return (
    <div className="action-center">
      <header className="page-header action-center-header">
        <div>
          <div className="action-center-eyebrow">{t('acties.eyebrow')}</div>
          <h1 className="page-title">{t('acties.titel')}</h1>
          <p className="page-subtitle">{t('acties.subtitel')}</p>
        </div>
        {!loading && total > 0 && (
          <div className="action-center-summary" aria-label={t('acties.samenvattingLabel')}>
            <span><strong>{urgent}</strong>{t('acties.nuOpvolgen')}</span>
            <span><strong>{exceptions}</strong>{t('acties.uitzonderingen')}</span>
          </div>
        )}
      </header>

      {error && <div className="error-msg action-center-error">{error}</div>}
      {loading ? <div className="loading-center"><div className="spinner" /></div> : total === 0 ? (
        <section className="card action-center-empty">
          <span className="action-center-empty-mark">✓</span>
          <h2>{t('acties.geenActies')}</h2>
          <p>{t('acties.geenActiesUitleg')}</p>
        </section>
      ) : (
        <div className="action-center-sections">
          <ActionSection title={t('acties.ptAanvragen')} count={data.ptAanvragen.length} tone="blue" empty={false}>
            {data.ptAanvragen.map(item => (
              <ActionRow key={item.id}
                title={item.client?.full_name || t('acties.onbekendeClient')}
                meta={t(item.status === 'rescheduled' ? 'acties.ptTegenvoorstel' : 'acties.ptAangevraagdVoor', { datum: formatDateTime(item.reschedule_proposed_at || item.requested_at) })}
                actionLabel={t('acties.beoordelen')}
                onAction={() => navigate('/coach/aanvragen')}
              />
            ))}
          </ActionSection>

          <ActionSection title={t('acties.prospectActies')} count={data.prospectActies.length} tone="amber" empty={false}>
            {data.prospectActies.map(item => {
              const overdue = amsterdamDateStr(item.next_action_at) < todayStr();
              return <ActionRow key={item.prospect_id}
                title={item.prospect.full_name || item.prospect.email}
                meta={item.next_action_note || t('acties.prospectActieZonderOmschrijving')}
                status={t(overdue ? 'acties.achterstalligSinds' : 'acties.vandaagOm', { datum: fmtDate(item.next_action_at), tijd: toAmsterdamTime(item.next_action_at) })}
                urgent={overdue}
                actionLabel={t('acties.openInPipeline')}
                onAction={() => navigate(`/coach/prospects?prospect=${item.prospect_id}`)}
              />;
            })}
          </ActionSection>

          <AgreementSection
            title={t('acties.afgewezenOvereenkomsten')}
            rows={data.afgewezenTokens}
            tone="red"
            dateKey="rejected_at"
            dateLabel={t('acties.afgewezenOp')}
            dismissType="rejected_token"
            dismissingId={dismissingId}
            onDismiss={handleDismiss}
            onOpenAgreement={clientId => navigate(`/coach/overeenkomst/opstellen/${clientId}`)}
          />

          <AgreementSection
            title={t('acties.verlopenOvereenkomsten')}
            rows={data.verlopenTokens}
            tone="orange"
            dateKey="expires_at"
            dateLabel={t('acties.verlopenOp')}
            dismissType="expired_token"
            dismissingId={dismissingId}
            onDismiss={handleDismiss}
            onOpenAgreement={clientId => navigate(`/coach/overeenkomst/opstellen/${clientId}`)}
          />

          <ActionSection title={t('acties.openstaandeIncasso')} count={data.incasso.length} tone="red" empty={false}>
            {data.incasso.map(invoice => (
              <ActionRow key={invoice.id}
                title={invoice.client?.full_name || t('acties.onbekendeClient')}
                meta={t('acties.incassoOmschrijving', { nummer: invoice.invoice_number, bedrag: euro(invoice.total_amount) })}
                actionLabel={t('acties.factuurBekijken')}
                onAction={() => navigate('/coach/facturen')}
              />
            ))}
          </ActionSection>
        </div>
      )}
    </div>
  );
}

function AgreementSection({ title, rows, tone, dateKey, dateLabel, dismissType, dismissingId, onDismiss, onOpenAgreement }) {
  return (
    <ActionSection title={title} count={rows.length} tone={tone} empty={false}>
      {rows.map(token => (
        <ActionRow key={token.id}
          title={token.client?.full_name || t('acties.onbekendeClient')}
          meta={token.rejection_reason || t('acties.overeenkomstOmschrijving', { datum: fmtDate(token[dateKey]) })}
          status={`${dateLabel} ${fmtDate(token[dateKey])}`}
          actionLabel={t('acties.overeenkomstOpstellen')}
          onAction={() => onOpenAgreement(token.client?.id)}
          secondary={
            <button className="btn btn-ghost btn-sm" onClick={() => onDismiss(dismissType, token.id)} disabled={dismissingId === `${dismissType}:${token.id}`}>
              {dismissingId === `${dismissType}:${token.id}` ? t('common.laden') : t('acties.nietMeerTonen')}
            </button>
          }
        />
      ))}
    </ActionSection>
  );
}

function ActionSection({ title, count, tone, children }) {
  if (count === 0) return null;
  return (
    <section className={`card action-center-section tone-${tone}`}>
      <header><h2>{title}</h2><span>{count}</span></header>
      <div className="action-center-list">{children}</div>
    </section>
  );
}

function ActionRow({ title, meta, status, urgent = false, actionLabel, onAction, secondary }) {
  return (
    <div className="action-center-row">
      <div className="action-center-row-copy">
        <strong>{title}</strong>
        <span>{meta}</span>
        {status && <small className={urgent ? 'is-urgent' : ''}>{status}</small>}
      </div>
      <div className="action-center-row-actions">
        {secondary}
        <button className="btn btn-secondary btn-sm" onClick={onAction}>{actionLabel}</button>
      </div>
    </div>
  );
}

// Review M10: dezelfde telling als de sidebar-badge (telOpenActies), met de
// expliciete sleutel-allowlist uit actiesUtils. Blind over Object.values
// tellen nam ook `nietGelogdeSessies` mee, die deze pagina bewust niet
// rendert — badge en lijst liepen daardoor uiteen.
function totalCount(data) {
  return telOpenActies(data);
}

function formatDateTime(value) {
  if (!value) return t('acties.datumOnbekend');
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(value));
}
